import { createSlice } from '@reduxjs/toolkit';

export interface ErrorStruct {
	code: number;
	error: string;
	result?: any;
}

export interface ErrorMessage {
	errors:	ErrorStruct[];
}
export interface TagDescriptor {
  tag?: string;
}

export interface TagInfo extends TagDescriptor {
  desc: string;
}

export interface TagsManager {
  tags: TagInfo[];
  is_new?: boolean;
  to_delete?: boolean;
}

export interface UrlInfo {
  type: string;
  url: string;
  tags: TagDescriptor[];
  expires_at?:		number;
  activates_at?:	number;
  is_invalid?:		boolean;
}

export interface ChannelBase {
  epg_id: string;
  old_epg_id: string;
  name: string;
  is_new?: boolean;
  to_delete?: boolean;
}

export interface ChannelDescriptor extends ChannelBase {
  channel_no: 		number;
  calculated_no?: 	number;
  is_invalid?:		boolean;
  ticked?:			boolean;
}

export interface ChannelInfoMaster {
  real_epg_id: string;
  languages:  string[];
  logo: string;
  urls: UrlInfo[];
}

export interface SelectOpt {
	value:			string;
	desc:			string;
}

export interface FieldDescriptor {
	field:			string;
	label:			string;
	placeholder:	string;
	desc:			string;
	type:			string;
	visibility:		string[];
	permissions?:	string[];
	features?:		string[];
	defval:			any;
	options:		SelectOpt[];
	acl?:			boolean;
	roles?:			string[];
	// Only does something while the Checkout is switched on (ticket #91 rev 2):
	// shown greyed, with a hint, while it is off.
	needsCheckout?:	boolean;
	// The opposite: hidden while the Checkout is switched on (Long Stay).
	hiddenWhileCheckoutOn?:	boolean;
}

// A field that clashes with the MW light Checkout (Long Stay) is hidden while
// the server switch is on; every other field is unaffected.
export function fieldHiddenByCheckoutOn(field: FieldDescriptor, checkoutOn: boolean): boolean {
	return !!field.hiddenWhileCheckoutOn && checkoutOn;
}

// §3.2 — a field that only matters with the Checkout on is greyed while the
// server switch is off; it stays visible, and every other field is unaffected.
export function fieldGreyedByCheckoutOff(field: FieldDescriptor, checkoutOff: boolean): boolean {
	return !!field.needsCheckout && checkoutOff;
}

export interface ChannelInfo extends ChannelInfoMaster, ChannelBase {
	ticked?:		boolean;
}

export interface ChannelPublic extends ChannelDescriptor, ChannelInfoMaster {
}

export interface ChannelManager {
  channels: ChannelInfo[];
}

export interface ChannelList extends ChannelBase {
  channels: 		ChannelDescriptor[];
  hidden:			boolean;
  tags:				TagDescriptor[];
  owner:			string;
  is_invalid?:		boolean;
}

export interface ChannelListManager /*extends ChannelBase*/ {
  lists: ChannelList[] | null;
}

export interface ChannelListDescriptor {
  id: string;
  start_from: number;
}

const channelsUrl = "/channels.json";
const channelListsUrl = "/channel_lists.json";

const initialState: ChannelManager = {
  channels:	[],
};

// Both channel reducers coalesce a missing/null payload to []. Go marshals a
// nil slice as JSON `null`, so a caller with no ViewChannels privilege (e.g. a
// local project user) gets a 200 with {"channels": null}. Assigning that
// verbatim made state.channels null, and consumers that iterate it — e.g.
// ChannelList's `for (const chan of channels.channels)` — threw
// "channels.channels is not iterable" and blew up the whole page.
// The `lists` reducers below already guarded this way; these two did not.
export const channelsSlice = createSlice({
  name: 'channels',
  initialState: {
	channels: [],
},
  reducers: {
    assign: (state, action) => {
      state.channels = action.payload?.channels ?? [];
    },
  },
});

export const hotelChannelsSlice = createSlice({
  name: 'hotelChannels',
  initialState: {channels: []},
  reducers: {
    assign: (state, action) => {
      state.channels = action.payload?.channels ?? [];
    },
  },
});

export const channelListsSlice = createSlice({
  name: 'channelLists',
  initialState: {lists: null} as ChannelListManager,
  reducers: {
    assign: (state, action) => {
      state.lists =	action.payload.lists
      				?	sortChannelLists(action.payload.lists)
      				:	[];
    },
  },
});

export const hotelChannelListsSlice = createSlice({
  name: 'hotelChannelLists',
  initialState: {lists: []} as ChannelListManager,
  reducers: {
    assign: (state, action) => {
      state.lists = action.payload.lists
      				?	sortChannelLists(action.payload.lists)
      				:	[];
    },
  },
});

function sortChannelLists(lists: ChannelList[]): ChannelList[] {
  lists.sort((a: ChannelList, b: ChannelList) => {
    if (a.name < b.name)
      return -1;
    if (a.name > b.name)
      return +1;
    
    return 0;
  });
  
  return lists;
}

export function getChannelsUrl(hotel_id?: string, global?: boolean) {
  return 	(	hotel_id &&
				hotel_id != ""
				?	channelsUrl + "?hotel_id=" + encodeURIComponent(hotel_id)
				:	channelsUrl
			) +
			(	global
				?	"&global=1"
				:	""
			);
}

export function getChannelListsUrl(hotel_id?: string, global?: boolean) {
  return 	(	hotel_id &&
				hotel_id != ""
				?	channelListsUrl + "?hotel_id=" + encodeURIComponent(hotel_id)
				:	channelListsUrl
			) +
			(	global
				?	"&global=1"
				:	""
			);
}

export function copyChannelManager(src: ChannelManager): ChannelManager {
  return {
    channels: src.channels.map((chan: ChannelInfo) => copyChannelInfo(chan)),
  };
}

export function copyChannelInfo(chan: ChannelInfo): ChannelInfo {
  return {
    epg_id:						chan.epg_id,
    old_epg_id:					chan.old_epg_id,
    name:						chan.name,
    is_new:						chan.is_new,
    to_delete:					chan.to_delete,
    real_epg_id:				chan.real_epg_id,
    languages:					(chan.languages || [])
                                .filter((lang: string) => lang != ''),
    logo:						chan.logo,
    urls:						copyUrls(chan.urls),
    ticked:						chan.ticked ? true : false,
  };
}

export function copyUrlInfo(url: UrlInfo): UrlInfo {
  return {
    type:						url.type,
    url:						url.url,
    tags:						copyTagDescriptors(url.tags),
    expires_at:					url.expires_at,
    activates_at:				url.activates_at,
    is_invalid:					url.is_invalid,
  };
}

export function getNewChannelInfo(): ChannelInfo {
  return {
    epg_id:						"",
    old_epg_id:					"",
    name:						"",
    is_new:						true,
    real_epg_id:				"",
    languages:					[],
    logo:						"",
    urls:						[],
  };
}

export function getNewChannelList(): ChannelList {
  return {
    epg_id:						"",
    old_epg_id:					"",
    name:						"",
    is_new:						true,
    channels: 					[],
    hidden:						false,
    tags:						[],
    owner:						"",
  };
}

export function copyTagDescriptors(tags?: TagDescriptor[]): TagDescriptor[] {
  if (!tags)
    return [];
  
  return tags.map((tag: TagDescriptor) => {
    return {tag: tag.tag};
  });
}

export function copyUrls(urls?: UrlInfo[]): UrlInfo[] {
  if (!urls) // === undefined)
    return [];
  
  return urls.map((url: UrlInfo) => copyUrlInfo(url))
}

export function copyChannelListDescriptors(descs: ChannelListDescriptor[]): ChannelListDescriptor[] {
  if (!descs)
    return [];
  
  return descs.map((desc: ChannelListDescriptor) => {
    return {
      id:					desc.id,
      start_from:			desc.start_from,
    }
  });
}

export function copyChannelDescriptor(cd: ChannelDescriptor): ChannelDescriptor {
    return {
      epg_id:				cd.epg_id,
      old_epg_id:			cd.old_epg_id,
      name:					cd.name,
      channel_no:			cd.channel_no,
      ticked:				cd.ticked ? true : false,
    };
}

export function copyChannelDescriptors(cds: ChannelDescriptor[]): ChannelDescriptor[] {
  if (!cds)
    return [];
  
  return cds.map((cd: ChannelDescriptor) => copyChannelDescriptor(cd));
}

export function copyChannelList(cl: ChannelList): ChannelList {
  return {
    epg_id:				cl.epg_id,
    old_epg_id:			cl.old_epg_id,
    name:				cl.name,
    channels: 			copyChannelDescriptors(cl.channels),
    hidden:				cl.hidden,
    tags:				copyTagDescriptors(cl.tags),
    owner:				cl.owner,
    is_invalid:			cl.is_invalid,
    is_new:				cl.is_new,
    to_delete:			cl.to_delete,
  };
}

export function getUrl(urls: UrlInfo[], type: string): UrlInfo | null {
	if (!urls)
	  return null;
	
	for (let url of urls) {
		if (	url.type == type &&
		   		url.url &&
		   		url.url != ""
		   ) {
			return url;
		}
	}
		
	return null;
}

export function  getField(fields: UrlInfo[], field: string): string {
	let url = getUrl(fields, field);

	return	url
			?	url.url
			:	"";
}


export function setField(fields: UrlInfo[], field: string, url: string, tags?: TagDescriptor[]): UrlInfo[] {
	let urls = copyUrls(fields);
	if (!urls)
	  urls = [];

	for(let i=0; i<urls.length; i++) {
	  if (urls[i].type !== field)
	    continue;
	  
	  urls[i].url = url;
	  return urls;
	}
	
	// Add new field
	urls.push({
	  type:			field,
      url:			url,
      tags:			tags
      				?	tags
      				:	[],
	});
	
	return urls;
}

export function getFieldExpiry(fields: UrlInfo[], field: string): number {
	if (!fields)
	  return 0;

	for (const url of fields) {
		if (url.type === field)
			return url.expires_at || 0;
	}

	return 0;
}

export function setFieldExpiry(fields: UrlInfo[], field: string, expires_at: number): UrlInfo[] {
	let urls = copyUrls(fields);
	if (!urls)
	  urls = [];

	const sanitized = expires_at > 0 ? expires_at : 0;

	for (let i = 0; i < urls.length; i++) {
		if (urls[i].type !== field)
			continue;

		urls[i].expires_at = sanitized;
		return urls;
	}

	urls.push({
		type:			field,
		url:			"",
		tags:			[],
		expires_at:		sanitized,
	});

	return urls;
}

export function getFieldActivation(fields: UrlInfo[], field: string): number {
	if (!fields)
		return 0;

	for (const url of fields) {
		if (url.type === field)
			return url.activates_at || 0;
	}

	return 0;
}

export function setFieldActivation(fields: UrlInfo[], field: string, activates_at: number): UrlInfo[] {
	let urls = copyUrls(fields);
	if (!urls)
	  urls = [];

	const sanitized = activates_at > 0 ? activates_at : 0;

	for (let i = 0; i < urls.length; i++) {
		if (urls[i].type !== field)
			continue;

		urls[i].activates_at = sanitized;
		return urls;
	}

	urls.push({
		type:			field,
		url:			"",
		tags:			[],
		activates_at:	sanitized,
	});

	return urls;
}

export function validateActivationExpiry(url: UrlInfo): boolean {
	const activatesAt = url.activates_at || 0;
	const expiresAt = url.expires_at || 0;
	if (activatesAt > 0 && expiresAt > 0 && activatesAt >= expiresAt)
		return false;
	return true;
}

export function validateUrlsActivationExpiry(urls: UrlInfo[]): boolean {
	if (!urls)
		return true;
	for (const url of urls) {
		if (!validateActivationExpiry(url))
			return false;
	}
	return true;
}

export function getCombinedFieldValue(roomFields: UrlInfo[], projFields: UrlInfo[], field: string): string {
	const roomValue = getField(roomFields, field);
	if (roomValue !== '')
		return roomValue;
	return getField(projFields, field);
}

export function checkNoError(mess: ErrorMessage | null): boolean {
	if (	!mess ||
  			!mess.errors ||
  			!mess.errors.length
  	   )
  	  return false;
	
	return	mess.errors[0].code == 0;
}

export function getChannelByEpgId(epg_id: string, channels: ChannelManager): ChannelInfo | null {
  // A slice can hold a null list (the API marshals an empty set as JSON null),
  // and callers chain several managers together - never iterate blindly.
  if (	!channels ||
  		!channels.channels
     )
    return null;
  
  for(const channel of channels.channels) {
    if (channel.epg_id != epg_id)
      continue;
    
    return channel;
  }
  
  return null;
}

export function getChannelListByEpgId(epg_id: string, channelLists: ChannelListManager): ChannelList | null {
  if (!channelLists.lists)
    return null;
  
  for(const list of channelLists.lists) {
    if (list.epg_id != epg_id)
      continue;
    
    return list;
  }
  
  return null;
}

// ---------------------------------------------------------------------------
// Screen Time — wire field names.
//
// The value fields live in the project's limits.urls and may be overridden
// per room in the room's fields — the same mechanism as network-error-message.
// Only the NAMES live here; the editor itself is pages/Projects/
// ScreenTimeSettings.tsx, which needs a minutes-only input, a time picker and a
// half-window warning that the generic FieldDescriptor renderer cannot express.
//
// There are deliberately no FieldDescriptors for them. A field marked
// `acl: true` emits one ACL entry per FIELD NAME, and revision 2 of the contract
// replaced the five per-field keys with the single shared `enable-screentime`
// toggle — which IS declared as a descriptor, in getWelcomeFields(), because
// that is the list the Manager ACL editor iterates. Without that entry the key
// would never be written and every room override would stay refused.
//
// The quota is stored in MINUTES. GET /screentime.json reports quota_seconds
// (stored minutes x 60) — never write seconds into the field.
// ---------------------------------------------------------------------------

export const UrlDisabled = "__disabled__";

export const screentimeAclKey = "enable-screentime";

// Gates the Statistics tab (app usage + screen time) at project and room
// level — separate from `screentimeAclKey`, which stays scoped to setting the
// screen time allowance/window/message. While this key is entirely absent
// (every project predating it), the grant falls back to the full screen-time
// grant so nothing changes on deploy; once written, "1" or anything else
// decides outright, and an explicit "" is a deny even where screen time is on.
// See canOverrideStatistics() in features/appusage/appUsageApi.ts.
export const statisticsAclKey = "enable-statistics";

// Order matters: quota, period, from, to, message, then the display fields.
//
// APPEND here, never insert — the names are index-exported below, and the
// first four are screentimeLimitFields, which is all "Remove limits" clears —
// the blocked screen fields are kept. `screentime-track` deliberately does NOT
// belong in this list: it is the recording opt-out, and clearing the limits
// must not silently switch recording back on.
export const screentimeFields = [
  "screentime-quota",
  "screentime-period",
  "screentime-from",
  "screentime-to",
  "screentime-message",
  "screentime-details",
  "screentime-colour",
  "screentime-title",
];

export const screentimeQuotaField	= screentimeFields[0];
export const screentimePeriodField	= screentimeFields[1];
export const screentimeFromField	= screentimeFields[2];
export const screentimeToField		= screentimeFields[3];
export const screentimeMessageField	= screentimeFields[4];
export const screentimeDetailsField	= screentimeFields[5];
export const screentimeColourField	= screentimeFields[6];
export const screentimeTitleField	= screentimeFields[7];
// The subset that decides whether the TV is blocked AT ALL. The three display
// fields say what the blocked screen looks like, never whether it appears, so
// any question of the form "does this scope restrict anything?" — the
// inherits-from-project warning above all — has to read this list and not the
// full one. A room that only picked its own background colour has set no limit.
export const screentimeLimitFields = screentimeFields.slice(0, 4);

export const languageSelectorAclKey = "enable-language-selector";

// Gates the Device Commands row (Restart / Standby) on a room's Info panel.
// Those commands reboot or sleep the guest's device through /skyway.json, so
// the grant is OFF while the key is absent — there is no older ACL to fall
// back on, and a project that never ticked it keeps the admin-only row it has
// today. Only the Android dongle reports the commands at all, so the row still
// needs the dongle check on top of this grant, and a SeeAllProjects-only user
// sees it read-only like every other control on that page.
export const deviceCommandsAclKey = "enable-device-commands";

// The row answers to that ACL alone, plus the admin bypass every ACL carries.
// No fallback to another key on purpose: a project that was never granted it
// keeps the admin-only row, and unticking the box (which clears the value)
// has to read as a deny rather than as "unset, ask someone else".
export function canRunDeviceCommands(isAdmin: boolean, acls?: UrlInfo[]): boolean {
  if (isAdmin)
    return true;

  return getField(acls || [], deviceCommandsAclKey) == "1";
}

export const MAX_LANGUAGE_PAIRS = 12;

export const MAX_LANGUAGE_LABEL_LENGTH = 64;

export function getUrlFields(): FieldDescriptor[] {
  return [
  	{	field:			"logo",
      	label:			"Welcome Logo",
      	placeholder:	"http://",
      	desc:			'URL for Welcome Logo',
      	type:			"checkbox",
      	defval:			"http://",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name", "hidden_user"],
    },
  	{	field:			"welcome",
      	label:			"Welcome Background",
      	placeholder:	"",
      	desc:			'URL for Welcome Background',
      	type:			"checkbox",
      	defval:			"http://",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name", "hidden_user"],
    },
  	{	field:			"welcome-movie",
      	label:			"Welcome Movie",
      	placeholder:	"",
      	desc:			'URL for Welcome Movie',
      	type:			"checkbox",
      	defval:			"http://",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name", "hidden_user"],
    },
  	{	field:			"welcome-virtual-channel",
      	label:			"Welcome Virtual Channel",
      	placeholder:	"",
      	desc:			'URL for Welcome Virtual Channel',
      	type:			"checkbox",
      	defval:			"http://",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name", "hidden_user"],
    },
  	{	field:			"guestmap-embedded",
      	label:			"Embedded Guestmap",
      	placeholder:	"",
      	desc:			'URL for Embedded Guestmap',
      	type:			"checkbox",
      	defval:			"http://",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name", "hidden_user"],
    },
	{	field:			"network-error-message",
      	label:			"Network Error Message",
      	placeholder:	"",
      	desc:			'whilw wifi is disconnect.Enter the error message that need to display in the App.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name", "hide_default", "textarea"],
      },
  ];
}

export function getWelcomeFields(): FieldDescriptor[] {
  return [
      {	field:			"enable-welcome",
      	label:			"Welcome Screen",
      	placeholder:	"",
      	desc:			'With this setting you can enable or disable welcome screen for a specific room, even if it has been disabled at the project level',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["room"],
      	options:		[],
      },
	   {field:			"enable-apps",
      	label:			"APPS",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	   {field:			"enable-channellist",
      	label:			"Channel list",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	   {field:			"enable-android-network-settings",
      	label:			"Android Network Settings",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
	  {	field:			"custom-button",
      	label:			"Custom button",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name"],
      },
	  {	field:			"custom-channel-list",
      	label:			"Channel List",
      	placeholder:	"",
      	desc:			'',
      	type:			"dropdown",
      	defval:			"",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["channellists_source"],
      },
	  {	field:			"enable-theme",
      	label:			"Theme",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	  {	field:			"enable-language",
      	label:			"Language",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	  {	field:			"enable-public-ip",
      	label:			"Public IP",
      	placeholder:	"",
      	desc:			'',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	  {	field:			screentimeAclKey,
      	label:			"Screen Time",
      	placeholder:	"",
      	desc:			'Lets a Manager set the screen time allowance, window and message per room. With this off, room-level overrides are ignored — for admins too.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	  {	field:			statisticsAclKey,
      	label:			"Statistics",
      	placeholder:	"",
      	desc:			'Lets a Manager see the Statistics tab (app usage and screen time) at project and room level. While off is unset, this follows the Screen Time grant above; setting it explicitly always wins.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	  {	field:			languageSelectorAclKey,
      	label:			"Channellist Language Selector",
      	placeholder:	"",
      	desc:			'Lets a Manager configure the name/TV-language/channel-list choices the guest picks from on the TV, at project level and per room. With this off, room-level overrides are ignored — for admins too.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
	  {	field:			deviceCommandsAclKey,
      	label:			"Dongle Device Commands",
      	placeholder:	"",
      	desc:			'Lets a Manager restart the device in a room or send it to standby, from the Info panel of that room. Only Android dongles report these commands, so no other device ever shows the row. Off unless ticked: without it the row stays admin-only.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		[],
      	options:		[],
      },
      {	field:			"operating-mode",
      	label:			"Set-Top-Box in Lobby",
      	placeholder:	"",
      	desc:			'If you are using a PrimeTime Set-Top-Box the box will show a Message with “Are you still watching?” after 3 hours. By checking this box, the message will not show up',
      	type:			"checkbox",
      	defval:			"unattended",
      	visibility:		["hotel", "room"],
      	acl:			true,
      	options:		[],
      },
      {	field:			"permanent-checkin",
      	label:			"Long Stay",
      	placeholder:	"",
      	desc:			'Disable checkout process for long stay residents',
      	type:			"checkbox",
      	defval:			"1",
      	visibility:		["hotel", "room"],
      	acl:			true,
      	// Hidden while the MW light Checkout is switched on (#91).
      	hiddenWhileCheckoutOn:	true,
      	options:		[],
      },
      // MW light (ticket #91, §2.2). Hotel level only — a room cannot decide
      // this — and admins only, so `acl: false` keeps it out of the Manager ACL
      // editor entirely and `roles` keeps the checkbox itself out of a
      // non-admin's Welcome Wizard. `needsCheckout` greys it while the
      // Checkout is switched off on the server: no keep list goes out then
      // (§3.2).
      {	field:			"auto-checkout-clear-netflix",
      	label:			"Automatic checkout also clears Netflix",
      	placeholder:	"",
      	desc:			'Only matters in hotels with MW light users. Unticked (the default), Android boxes keep Netflix at reboot and standby, and housekeeping clears it with Checkout. Philips, Samsung and LG always clear everything.',
      	type:			"checkbox",
      	defval:			"1",
      	visibility:		["hotel"],
      	acl:			false,
      	roles:			["admin"],
      	needsCheckout:	true,
      	options:		[],
      },

      {	field:			"enable-epg",
      	label:			"EPG",
      	placeholder:	"",
      	desc:			'With this setting you can enable the EPG, HUD menu, and GUIDE buttons. Without this enabled, only Chanel UP / DOWN & number entry can be used to switch channels.',
      	type:			"checkbox",
      	defval:			"1",
      	visibility:		["hotel", "room"],
      	acl:			true,
      	//permissions:	["admin"],
      	options:		[],
      },
      {	field:			"enable-replaytv",
      	label:			"Replay TV",
      	placeholder:	"",
      	desc:			'With this setting you can enable the selection of recorded content via guide, or using Left/Right keys',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	//permissions:	["admin"],
      	options:		[],
      },
      {	field:			"enable-tv",
      	label:			"Live TV",
      	placeholder:	"",
      	desc:			'With this setting you are enabling Live TV button in the main menu. Without this there will be no Live TV.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"enable-radio",
      	label:			"Radio",
      	placeholder:	"",
      	desc:			'With this setting you are enabling Radio button in the main menu. Without this there will be no Radio.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"enable-casting",
      	label:			"Casting",
      	placeholder:	"",
      	desc:			'With this setting you are enabling Casting button in the Main menu. Samsung TVs load the matching clone settings after a restart.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"cloud-casting",
      	label:			"Cloud Casting",
      	placeholder:	"",
      	desc:			'With this setting the casting button on main menu switches to Cloud casting channel',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"enable-guestmap",
      	label:			"Guestmap",
      	placeholder:	"",
      	desc:			'With this setting you are enabling Guestmap button in the Main menu',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"show-hdmi1",
      	label:			"HDMI-1",
      	placeholder:	"",
      	desc:			'With this setting you are adding the HDMI-1 input Menu from the Main menu. When clicking on this button, the HDMI1 Source will be shown. Important, an active HDMI device need to be connected to HDMI1, to show the input. Connected device must be not in standby.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name"],
      },
      {	field:			"show-hdmi2",
      	label:			"HDMI-2",
      	placeholder:	"",
      	desc:			'With this setting you are adding the HDMI-2 input Menu from the Main menu. When clicking on this button, the HDMI1 Source will be shown. Important, an active HDMI device need to be connected to HDMI1, to show the input. Connected device must be not in standby.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name"],
      },
      {	field:			"show-hdmi3",
      	label:			"HDMI-3",
      	placeholder:	"",
      	desc:			'With this setting you are adding the HDMI-3 input Menu from the Main menu. When clicking on this button, the HDMI1 Source will be shown. Important, an active HDMI device need to be connected to HDMI1, to show the input. Connected device must be not in standby.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	features:		["set_name"],
      },
      {	field:			"show-wifiap",
      	label:			"AP Mode",
      	placeholder:	"",
      	desc:			'With this setting you are enabling TV\'s inbuilt Wifi hotspot.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"qr-wifi",
      	label:			"Scan for WiFi",
      	placeholder:	"",
      	desc:			'With this setting you are enabling QR code for WiFi.\nIt uses values of below 2 fields "WiFi SSID" & "WiFi Key" to generate the QR code',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"wifi-ssid",
      	label:			"WiFi SSID",
      	placeholder:	"",
      	desc:			'"Scan for WiFi" uses this to generate the QR code',
      	type:			"input",
      	defval:			"",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"wifi-key",
      	label:			"WiFi Key",
      	placeholder:	"",
      	desc:			'"Scan for WiFi" uses this to generate the QR code',
      	type:			"input",
      	defval:			"",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      /*{	field:			"no-standby",
      	label:			"Disable standby for newer Tizen models",
      	placeholder:	"",
      	desc:			'With this setting you are Disable Standby, Only for Tizen with dcmp-server defined.',
      	type:			"checkbox",
      	defval:			"1",
      	visibility:		["hotel", "room"],
      	options:		[],
      },*/
      {	field:			"resume_last_channel",
      	label:			"Remember last watched Channel",
      	placeholder:	"",
      	desc:			'By checking this box, we will remember for all connected TVs what channel has been watched the last and load it back if the tv restarts. For example, if you watched RAI1 HD as last channel, the tv will remember it, and start RAI1 HD if you restart the tv. If this box is not checked, we will always load the first channel in the channel list',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"enable-source",
      	label:			"Source button",
      	placeholder:	"",
      	desc:			'With this setting you can enable source button on remote to switch to HDMI. Only for Tizen TVs.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"disable-teletext",
      	label:			"Disable Teletext",
      	placeholder:	"",
      	desc:			'With this setting you can disable Teletext on all platforms. When enabled (checked), Teletext will not be shown on the TV. When disabled (unchecked), Teletext will be shown.',
      	type:			"checkbox",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      },
      {	field:			"user-disability",
      	label:			"Select user disability",
      	placeholder:	"",
      	desc:			'With these two settings you can define if the user in this room as hearing problems or visual problems. By selecting Visual, the subtitles will show automatically if available on this channel. By selecting Audio, the audio description track of the audio will be selected, if available on this channel. Be aware, selecting the Audio option could select the second language of this channel, for example the original audio English of a movie',
      	type:			"dropdown",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[
      	  {	value: "",				desc: "Off"},
      	  {	value: "audio",			desc: "Audio"},
      	  {	value: "visual",		desc: "Visual"},
      	  {	value: "audio,visual",	desc: "Audio & Visual"},
      	],
      },
      {	field:			"available-apps",
      	label:			"3rd party apps in welcome screen",
      	placeholder:	"",
      	desc:			'List of available 3rd party apps in welcome screen.',
      	type:			"text",
      	defval:			"1",
      	acl:			true,
      	visibility:		["hotel", "room"],
      	options:		[],
      	//roles:			["admin"],
      },
  ];
}


